import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomButton, LabelAndValue } from '@medtronic/pecc-react-component-library-js';
import { Box, FormControlLabel, Modal, Stack, Switch, Typography } from '@mui/material';
import { DatasetCreationEnum, DatasetStatus } from '~/api/types';
import { useGetDatasetEnumsQuery } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { switchBlockStyles } from '~/components/DeleteConfirmationModal/styles';
import { modalActionBlockStyles, modalSmallContainerStyle } from '~/components/styles';
import { detailsItemStyles } from './styles';

interface ChangeStatusModalProps {
  open: boolean;
  onClose: () => void;
  selectedStatus: DatasetStatus | null;
}

const ChangeStatusModal: FC<ChangeStatusModalProps> = ({ open, onClose, selectedStatus }) => {
  const { data: locationsData } = useGetDatasetEnumsQuery({ enumKey: DatasetCreationEnum.GEO_LOCATION });
  const [checked, setChecked] = useState<boolean>(false);
  const { setValue } = useFormContext();
  const { watch } = useFormContext();
  const formValues = watch();

  const handleClose = () => {
    setChecked(false);
    onClose();
  };

  const onStatusChange = () => {
    setValue('status', selectedStatus, {
      shouldDirty: true,
      shouldValidate: true,
    });
    handleClose();
  };

  const dataConfig = [
    { label: 'dataset name', value: formValues?.name },
    { label: 'dataset status', value: formValues?.status },
    {
      label: 'region',
      value: !formValues?.geo_location_tags
        ? '—'
        : locationsData
            ?.filter(({ key }) => formValues?.geo_location_tags?.includes(key))
            .map(({ value }) => value.replace('GeoLocation_', ''))
            .join(', '),
    },
    { label: 'ou owner', value: formValues?.source_ou },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalSmallContainerStyle}>
        <ModalHeader onClose={handleClose} title={['Change the Dataset Status']} />
        <Box sx={detailsItemStyles}>
          {dataConfig.map(({ label, value }) => (
            <Box style={{ wordBreak: 'break-word', flex: 1 }} key={label}>
              <LabelAndValue label={label} value={value || '—'} />
            </Box>
          ))}
        </Box>
        <Stack pt={1} pb={3} px={3} spacing={2}>
          <Typography>
            Are you sure you wanted to change the dataset status to <b>“{selectedStatus}”</b> ?
          </Typography>
          <FormControlLabel
            labelPlacement="end"
            label={
              selectedStatus === DatasetStatus.LOCKED_SEARCHABLE
                ? 'I confirm a proper use and privacy memo has been added'
                : 'I understand and wish to proceed with this action'
            }
            sx={switchBlockStyles}
            control={
              <Switch onClick={(e) => e.stopPropagation()} onChange={() => setChecked(!checked)} sx={{ mx: '1rem' }} />
            }
          />
        </Stack>
        <Box sx={modalActionBlockStyles}>
          <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
          <CustomButton label="Change" disabled={!checked} onClick={onStatusChange} />
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangeStatusModal;
